import { useState, type MouseEvent } from 'react';
import type { ControlPin } from '@sss/form-engine';
import { Button, Dialog, Field, Icon, Input } from '../../components';
import { useI18n } from '../../lib/i18n';

export function FloorplanPinModal({
  open,
  controlCode,
  initialPin,
  onClose,
  onSave,
}: {
  open: boolean;
  controlCode: string;
  initialPin?: ControlPin;
  onClose: () => void;
  onSave: (pin: ControlPin | undefined) => void;
}) {
  const { t } = useI18n();
  const [x, setX] = useState<number>(initialPin?.x ?? 50);
  const [y, setY] = useState<number>(initialPin?.y ?? 50);
  const [zone, setZone] = useState<string>(initialPin?.zone ?? '');

  const handleMapClick = (e: MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = Math.max(2, Math.min(98, Math.round(((e.clientX - rect.left) / rect.width) * 100)));
    const ny = Math.max(2, Math.min(98, Math.round(((e.clientY - rect.top) / rect.height) * 100)));
    setX(nx);
    setY(ny);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={`${t('ui.excellence.pin.modalTitle')} — ${controlCode}`}
      actions={
        <>
          {initialPin && (
            <Button
              variant="ghost"
              onClick={() => {
                onSave(undefined);
                onClose();
              }}
            >
              {t('ui.excellence.pin.clear')}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t('ui.common.cancel')}
          </Button>
          <Button
            icon="mapPin"
            onClick={() => {
              onSave({ x, y, zone: zone.trim() || undefined });
              onClose();
            }}
          >
            {t('ui.common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-muted">{t('ui.excellence.pin.modalHelp')}</p>
        <div className="relative overflow-hidden rounded-xl border border-border bg-surface-2">
          <svg
            viewBox="0 0 400 260"
            className="h-60 w-full cursor-crosshair select-none"
            onClick={handleMapClick}
            role="img"
            aria-label={t('ui.excellence.pin.modalTitle')}
          >
            {/* Outer Perimeter Boundary */}
            <rect x="16" y="16" width="368" height="228" rx="8" fill="none" stroke="currentColor" strokeOpacity="0.35" strokeWidth="2.5" strokeDasharray="6 4" />
            <text x="26" y="32" fontSize="10" fill="currentColor" opacity="0.55" fontWeight="600">
              NORTH PERIMETER FENCE
            </text>
            {/* North Gate & Guard Post */}
            <rect x="175" y="10" width="50" height="16" rx="3" fill="currentColor" fillOpacity="0.15" stroke="currentColor" strokeOpacity="0.5" />
            <text x="200" y="21" fontSize="8" textAnchor="middle" fill="currentColor" fontWeight="700">
              MAIN GATE / POST 1
            </text>
            {/* Main Facility Building */}
            <rect x="70" y="60" width="180" height="120" rx="6" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.8" />
            <text x="160" y="120" fontSize="11" textAnchor="middle" fill="currentColor" opacity="0.7" fontWeight="600">
              MAIN OPERATIONS BUILDING
            </text>
            {/* Warehouse / Loading Bay */}
            <rect x="270" y="60" width="90" height="120" rx="6" fill="currentColor" fillOpacity="0.08" stroke="currentColor" strokeOpacity="0.45" strokeWidth="1.8" />
            <text x="315" y="120" fontSize="10" textAnchor="middle" fill="currentColor" opacity="0.7" fontWeight="600">
              LOADING BAY
            </text>
            {/* South Perimeter / Parking */}
            <text x="200" y="234" fontSize="10" textAnchor="middle" fill="currentColor" opacity="0.55" fontWeight="600">
              SOUTH PERIMETER &amp; VEHICLE YARD
            </text>

            {/* Dropped Hazard Pin */}
            <g transform={`translate(${(x / 100) * 400}, ${(y / 100) * 260})`}>
              <circle r="14" fill="#dc2626" fillOpacity="0.22" />
              <circle r="6" fill="#dc2626" stroke="#ffffff" strokeWidth="2" />
              <text y="-12" fontSize="10" textAnchor="middle" fill="#dc2626" fontWeight="700">
                {controlCode}
              </text>
            </g>
          </svg>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <Icon name="mapPin" size={14} className="text-danger" />
          <span>{t('ui.excellence.pin.pinned', { x, y })}</span>
        </div>
        <Field label={t('ui.excellence.pin.zonePlaceholder')}>
          {(fid) => <Input id={fid} value={zone} placeholder={t('ui.excellence.pin.zonePlaceholder')} onChange={(e) => setZone(e.target.value)} />}
        </Field>
      </div>
    </Dialog>
  );
}
